import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShieldCheck, IdCard, BookUser, FileText, ArrowRight, AlertTriangle, Newspaper, ListChecks } from "lucide-react";
import { PageLoading, PageHeader, fmtDate } from "@/components/shared";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { ComplianceNewsList } from "@/components/ComplianceNewsWidget";
import WorkerChecklistDialog from "@/components/WorkerChecklistDialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function Card({ icon: Icon, label, value, sub, tone = "text-navy" }) {
  return (
    <div className="card-soft p-5">
      <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /><span className="overline">{label}</span></div>
      <div className={`mt-2 font-heading text-2xl font-extrabold ${tone}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

function NewsWidget() {
  const { t } = useTranslation();
  return (
    <div className="card-soft mt-6 p-6" data-testid="worker-news-widget">
      <div className="mb-1 flex items-center gap-2"><Newspaper className="h-5 w-5 text-cyan-dark" /><h3 className="font-heading text-lg font-bold text-navy">{t("ai.newsTitle")}</h3></div>
      <p className="mb-4 text-sm text-slate-500">{t("ai.newsSubWorker")}</p>
      <ComplianceNewsList endpoint="/worker/compliance-news" limit={5} />
    </div>
  );
}

export default function WorkerOverview() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  useEffect(() => { api.get("/worker/overview").then((r) => setData(r.data)); }, []);
  useEffect(() => { api.get("/worker/profile-checklist").then((r) => setChecklist(r.data)).catch(() => {}); }, []);
  if (!data) return <PageLoading />;

  if (data.source_type === "SUBCONTRACTOR") {
    const w = data.worker;
    return (
      <div data-testid="worker-overview">
        <PageHeader title={t("worker.statusTitle")} subtitle={t("worker.welcome", { name: user?.first_name })} />
        <div className="card-soft p-6" data-testid="sub-worker-card">
          <div className="flex items-center gap-2 text-slate-400"><IdCard className="h-4 w-4" /><span className="overline">{t("worker.workerId")}</span></div>
          <div className="mt-1 font-heading text-xl font-extrabold text-navy" data-testid="worker-id-value">{w.worker_id}</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div><div className="overline text-slate-400">{t("worker.employer")}</div><div className="text-sm font-medium text-navy">{w.subcontractor_name || "—"}</div></div>
            <div><div className="overline text-slate-400">{t("worker.deploymentCompany")}</div><div className="text-sm font-medium text-navy">{w.deployment_company || "—"}</div></div>
            <div><div className="overline text-slate-400">{t("worker.currentSite")}</div><div className="text-sm font-medium text-navy">{w.current_site || "—"}</div></div>
            <div><div className="overline text-slate-400">{t("worker.documents")}</div><div className="text-sm font-medium text-navy">{data.document_count}</div></div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/app/worker/documents" className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-navy hover:bg-slate-50">{t("worker.documents")} <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/app/worker/deadlines" className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-navy hover:bg-slate-50">{t("worker.viewDeadlines")}</Link>
        </div>
        <NewsWidget />
      </div>
    );
  }

  const e = data.employee;
  const authValid = (e.work_authorization_status || "").toLowerCase().includes("authorized") && !(e.work_authorization_status || "").toLowerCase().includes("not");
  return (
    <div data-testid="worker-overview">
      <PageHeader title={t("worker.statusTitle")} subtitle={t("worker.welcome", { name: user?.first_name })} />
      {e.worker_id && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5" data-testid="worker-id-value">
          <IdCard className="h-4 w-4 text-slate-400" />
          <span className="overline text-slate-400">{t("worker.workerId")}</span>
          <span className="font-heading text-sm font-bold text-navy">{e.worker_id}</span>
        </div>
      )}
      {(() => {
        const ok = e.compliance_status === "COMPLIANT";
        return (
          <div className={`mb-6 flex items-start gap-4 rounded-2xl border p-5 ${ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="worker-status-hero">
            <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"} text-white`}>
              {ok ? <ShieldCheck className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
            </span>
            <div className="min-w-0">
              <div className={`font-heading text-lg font-extrabold ${ok ? "text-emerald-800" : "text-amber-800"}`}>{ok ? t("worker.allGood") : t("worker.actionNeeded")}</div>
              <p className={`mt-0.5 text-sm ${ok ? "text-emerald-700" : "text-amber-700"}`}>{ok ? t("worker.allGoodSub") : t("worker.actionNeededSub")}</p>
              {!ok && (
                <button onClick={() => setShowChecklist(true)} className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-amber-900 underline decoration-amber-400 underline-offset-2 hover:text-amber-950" data-testid="worker-hero-action">
                  {e.next_action} <ListChecks className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        );
      })()}
      {checklist && checklist.source_type === "INTERNAL" && !checklist.complete && (() => {
        const items = checklist.items || [];
        const done = items.filter((i) => i.done).length;
        const pct = items.length ? Math.round((done / items.length) * 100) : 0;
        return (
          <button onClick={() => setShowChecklist(true)} className="mb-6 block w-full rounded-2xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-cyan/50" data-testid="worker-profile-progress">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="font-heading text-sm font-bold text-navy">{t("checklist.progress", { pct })}</span>
              <span className="text-xs font-semibold text-cyan-dark">{done}/{items.length} · {t("checklist.viewList")}</span>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div className={`h-full rounded-full transition-[width] duration-700 ${pct >= 75 ? "bg-emerald-500" : pct >= 40 ? "bg-cyan" : "bg-amber-500"}`} style={{ width: `${pct}%` }} data-testid="worker-progress-bar" />
            </div>
          </button>
        );
      })()}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card icon={ShieldCheck} label={t("worker.workAuth")} value={authValid ? t("worker.valid") : (e.work_authorization_status || "—")} tone={authValid ? "text-emerald-600" : "text-navy"} />
        <Card icon={IdCard} label={t("worker.permit")} value={fmtDate(e.permit_valid_until)} sub={e.permit_days_remaining != null ? t("worker.daysRemaining", { n: e.permit_days_remaining }) : t("worker.noDate")} />
        <Card icon={BookUser} label={t("worker.passport")} value={fmtDate(e.passport_valid_until)} sub={e.passport_days_remaining != null ? t("worker.daysRemaining", { n: e.passport_days_remaining }) : t("worker.noDate")} />
        <Card icon={FileText} label={t("worker.documents")} value={data.document_count} sub={t("worker.uploaded")} />
      </div>
      <div className="card-soft mt-6 p-6">
        <div className="overline text-slate-400">{t("worker.nextAction")}</div>
        <div className="mt-2 font-heading text-lg font-bold text-cyan-dark">{e.next_action}</div>
        <div className="mt-4 flex flex-wrap gap-3">
          <button onClick={() => setShowChecklist(true)} className="inline-flex items-center gap-1 rounded-full bg-cyan px-4 py-2 text-sm font-medium text-white hover:bg-cyan-dark" data-testid="worker-open-checklist">{t("checklist.viewList")} <ListChecks className="h-4 w-4" /></button>
          <Link to="/app/worker/documents" className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-navy hover:bg-slate-50" data-testid="worker-goto-docs">{t("worker.uploadDocs")} <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/app/worker/deadlines" className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-navy hover:bg-slate-50">{t("worker.viewDeadlines")}</Link>
        </div>
      </div>
      <NewsWidget />
      <WorkerChecklistDialog open={showChecklist} onClose={() => setShowChecklist(false)} />
    </div>
  );
}
